import { useEffect, useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Conversa,
  MensagemChat,
} from '../services/mockData';
import {
  getConversasChat,
  parseMensagensChat,
} from '../services/dataService';
import { ref, onValue, query, orderByChild } from '../services/phpDataAdapter';
import { database } from '../config/backend';

// Função para mostrar notificação web
function showWebNotification(title: string, body: string, conversationId: string) {
  // Verificar se o navegador suporta notificações
  if (!('Notification' in window)) {
    console.log('Este navegador não suporta notificações');
    return;
  }

  // Verificar permissão
  if (Notification.permission === 'granted') {
    const notification = new Notification(title, {
      body,
      icon: '/logo192.png', // Ajuste para o caminho do seu ícone
      badge: '/logo192.png',
      tag: conversationId, // Evita duplicatas
      requireInteraction: false,
    });

    notification.onclick = () => {
      window.focus();
      notification.close();
    };
  }
}

export function useChat(conversaId: string | null) {
  const { user } = useAuth();
  const [conversas, setConversas] = useState<Conversa[]>([]);
  const [mensagens, setMensagens] = useState<MensagemChat[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesError, setMessagesError] = useState<string | null>(null);
  const lastMessageCountRef = useRef<number>(0);
  const isFirstLoadRef = useRef<boolean>(true);

  // Carrega lista de conversas do nutricionista
  useEffect(() => {
    let isMounted = true;

    async function loadConversas() {
      if (!user) {
        if (isMounted) {
          setConversas([]);
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const data = await getConversasChat(user.uid);
        if (isMounted) {
          setConversas(data);
        }
      } catch {
        if (isMounted) {
          setError('Não foi possível carregar as conversas.');
          setConversas([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadConversas();

    return () => {
      isMounted = false;
    };
  }, [user]);

  // Carrega mensagens da conversa selecionada com listener em tempo real
  useEffect(() => {
    let isMounted = true;
    let unsubscribe: (() => void) | null = null;

    async function setupMessageListener() {
      if (!conversaId) {
        if (isMounted) {
          setMensagens([]);
          setMessagesLoading(false);
          setMessagesError(null);
          lastMessageCountRef.current = 0;
          isFirstLoadRef.current = true;
        }
        return;
      }

      setMessagesLoading(true);
      setMessagesError(null);
      setMensagens([]);
      lastMessageCountRef.current = 0;
      isFirstLoadRef.current = true;

      try {
        // Configurar listener em tempo real
        const messagesRef = ref(database, `messages/${conversaId}`);
        const messagesQuery = query(messagesRef, orderByChild('createdAt'));

        unsubscribe = onValue(messagesQuery, (snapshot) => {
          if (!isMounted) return;

          const data = parseMensagensChat(
            snapshot.exists() ? snapshot.val() : null,
            conversaId
          );

          // Verificar se há novas mensagens (apenas após o carregamento inicial)
          if (!isFirstLoadRef.current && data.length > lastMessageCountRef.current) {
            const newMessagesCount = data.length - lastMessageCountRef.current;
            const latestMessage = data[data.length - 1];

            // Mostrar notificação apenas se a última mensagem não for do usuário atual
            if (latestMessage && latestMessage.autor !== 'nutri') {
              showWebNotification(
                'Nova mensagem no chat',
                latestMessage.conteudo.substring(0, 50) + (latestMessage.conteudo.length > 50 ? '...' : ''),
                conversaId
              );
            }
          }

          lastMessageCountRef.current = data.length;
          isFirstLoadRef.current = false;

          if (isMounted) {
            setMensagens(data);
            setMessagesLoading(false);
            setMessagesError(null);
          }
        }, (error) => {
          console.error('Erro ao carregar mensagens:', error);
          if (isMounted) {
            setMensagens([]);
            setMessagesLoading(false);
            setMessagesError('Não foi possível carregar as mensagens desta conversa.');
          }
        });
      } catch (error) {
        console.error('Erro ao configurar listener:', error);
        if (isMounted) {
          setMensagens([]);
          setMessagesLoading(false);
          setMessagesError('Não foi possível abrir esta conversa.');
        }
      }
    }

    setupMessageListener();

    return () => {
      isMounted = false;
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [conversaId]);

  return { conversas, mensagens, loading, error, messagesLoading, messagesError };
}

